import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const LANG_CONFIG = {
  zh: { flag: '🇨🇳', label: '中文' },
  en: { flag: '🇺🇸', label: 'EN' },
};

export default function Navbar() {
  const { t, i18n } = useTranslation('common');
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [langAnimating, setLangAnimating] = useState(false);
  const langMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const navLinks = [
    { label: t('nav.brandStory'), href: '/about' },
    { label: t('nav.technology'), href: '/technology' },
    { label: t('nav.products'), href: '/products' },
    { label: t('nav.sustainability'), href: '/sustainability' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  // Click outside to close lang menu
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target as Node)) {
        setLangMenuOpen(false);
      }
    };
    if (langMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [langMenuOpen]);

  const toggleLang = (lang: string) => {
    if (lang === i18n.language) {
      setLangMenuOpen(false);
      return;
    }
    setLangAnimating(true);
    setTimeout(() => {
      i18n.changeLanguage(lang);
      setLangAnimating(false);
      setLangMenuOpen(false);
    }, 200);
  };

  const currentLang = (i18n.language === 'zh' ? 'zh' : 'en') as 'zh' | 'en';
  const currentConfig = LANG_CONFIG[currentLang];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.06)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="h-8 flex items-center">
              <img
                src="https://storage.readdy-site.link/project_files/f5d75575-149e-42fd-8f5e-baf597e2961c/14b65f89-5db4-4044-8463-e06c231de31a_unnamed.png?v=45f66e64fb7ba855e9bbff4adf44ae01"
                alt={t('brand.name')}
                className="h-full w-auto object-contain"
              />
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-lg font-semibold tracking-tight text-brand-charcoal">
                {t('brand.name')}
              </span>
              <span className="text-xs font-medium tracking-wide text-brand-gray-light">
                {t('brand.nameEn')}
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm font-medium text-brand-stone hover:text-brand-charcoal transition-colors relative group whitespace-nowrap"
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-brand-mint-dark transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
          </div>

          {/* Desktop Right: Lang + CTA */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Switcher */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium transition-all duration-300 whitespace-nowrap ${
                  langMenuOpen
                    ? 'bg-brand-mint-light text-brand-mint-dark'
                    : 'bg-brand-charcoal/5 text-brand-gray hover:bg-brand-charcoal/10'
                } ${langAnimating ? 'scale-95 opacity-70' : 'scale-100 opacity-100'}`}
              >
                <span className="text-base leading-none">{currentConfig.flag}</span>
                <span>{currentConfig.label}</span>
                <i
                  className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${
                    langMenuOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Dropdown */}
              <div
                className={`absolute top-full right-0 mt-2 bg-white rounded-xl shadow-lg border border-brand-gray-pale/30 py-1.5 min-w-[120px] z-50 transition-all duration-300 origin-top-right ${
                  langMenuOpen
                    ? 'opacity-100 scale-100 translate-y-0'
                    : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
                }`}
              >
                {(['zh', 'en'] as const).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => toggleLang(lang)}
                    className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${
                      currentLang === lang
                        ? 'text-brand-mint-dark font-medium bg-brand-mint-light/40'
                        : 'text-brand-gray hover:bg-brand-cream'
                    }`}
                  >
                    <span className="text-base leading-none">{LANG_CONFIG[lang].flag}</span>
                    <span>{LANG_CONFIG[lang].label}</span>
                    {currentLang === lang && (
                      <i className="ri-check-line text-xs ml-auto text-brand-mint-dark" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            <Link
              to="/products"
              className="flex items-center gap-2 px-5 py-2.5 bg-brand-charcoal text-white text-sm font-medium rounded-full hover:bg-brand-stone transition-colors whitespace-nowrap"
            >
              <i className="ri-flask-line text-sm" />
              {t('nav.cta')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-brand-charcoal"
          >
            <i
              className={
                mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'
              }
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-white/98 backdrop-blur-lg shadow-lg transition-all duration-300 overflow-hidden ${
          mobileOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-4 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="block py-3 text-sm font-medium text-brand-stone hover:text-brand-charcoal transition-colors border-b border-brand-gray-pale/50"
            >
              {link.label}
            </Link>
          ))}

          {/* Mobile Lang Switch */}
          <div className="py-3 border-b border-brand-gray-pale/50">
            <span className="text-xs text-brand-gray-light block mb-2">
              Language
            </span>
            <div className="flex items-center gap-2 bg-brand-cream/50 rounded-xl p-1.5">
              {(['zh', 'en'] as const).map((lang) => (
                <button
                  key={lang}
                  onClick={() => {
                    if (lang !== currentLang) {
                      toggleLang(lang);
                    }
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    currentLang === lang
                      ? 'bg-white text-brand-charcoal shadow-sm'
                      : 'text-brand-gray hover:text-brand-charcoal'
                  }`}
                >
                  <span className="text-base leading-none">
                    {LANG_CONFIG[lang].flag}
                  </span>
                  <span>{LANG_CONFIG[lang].label}</span>
                </button>
              ))}
            </div>
          </div>

          <Link
            to="/products"
            className="block mt-4 py-3 px-4 bg-brand-charcoal text-white text-sm font-medium rounded-full text-center"
          >
            {t('nav.cta')}
          </Link>
        </div>
      </div>
    </nav>
  );
}